import type { JSX } from "react";
import { useMemo } from "react";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import { SMTEntryType } from "@/constants/SMT";
import { SMTTestID } from "@/constants/TestID";
import { useAppSelector } from "@/store/hooks";
import {
  selectEntryPhotoUri,
  selectUploadingEntryId,
} from "@/store/reducers/smtPhotoUpload";
import type { SMTFoodEntryDetailsProps } from "@/ui/components/smt/food/SMTFoodEntryDetails";
import SMTFoodEntryDetails from "@/ui/components/smt/food/SMTFoodEntryDetails";
import SMTEntryImagePreview from "@/ui/components/smt/ViewSMTEntries/SMTEntryImagePreview";
import { Avatar, TextTag } from "@/ui/core";
import { HStack, VStack } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";

const BaseTestID = SMTTestID.SMTEntry;

// as this is expanded to new entry types, this would be Type1 | Type2 | Type 3;
// similar to how scheduling handles appointments vs groupClassEvents polymorphically
type SMTEntryDetailsProps = SMTFoodEntryDetailsProps;

interface SMTEntryDetails {
  entryDetailsProps: Omit<SMTEntryDetailsProps, "testID">;
  entryType: SMTEntryType;
}

interface SMTEntryProps {
  avatarUserId?: string;
  avatarUserName?: string;
  avatarUserType?: BaseUserType;
  details: SMTEntryDetails;
  onOverrideOpenImageFullScreen?: () => void;
  photoContentType?: string;
  photoUrl?: string;
  smtEntryId: string;
  thumbnailUrl?: string;
  time?: string;
}

// While splitting this out into its own ComponentType like this may seem unnecessary,
// given that this basically just casts and passes props through as a no-op,
// this is done to satisfy react/no-unstable-nested-components and prevent unnecessary/unstable
// re-renders (or re-render loops) if this ComponentType definition were inside SMTEntry directly
const MappedMealEntryComponent = (
  entryDetailsProps: SMTEntryDetailsProps,
): JSX.Element => {
  const foodEntryDetailsProps =
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- necessary here to support polymorphic entry types
    entryDetailsProps as SMTFoodEntryDetailsProps;
  const { foodComment, foodDescription, foodType, isNewFood, testID } =
    foodEntryDetailsProps;
  return (
    <SMTFoodEntryDetails
      foodComment={foodComment}
      foodDescription={foodDescription}
      foodType={foodType}
      isNewFood={isNewFood}
      testID={testID}
    />
  );
};

const SMTEntry = ({
  avatarUserId,
  avatarUserName,
  avatarUserType,
  details,
  onOverrideOpenImageFullScreen,
  photoContentType,
  photoUrl,
  smtEntryId,
  thumbnailUrl,
  time,
}: SMTEntryProps) => {
  const baseTestID = joinTestIDs(BaseTestID, smtEntryId);
  const uploadingEntryId = useAppSelector(selectUploadingEntryId);
  const localPhotoUri = useAppSelector(selectEntryPhotoUri(smtEntryId));
  const isUploading = uploadingEntryId === smtEntryId;

  const previewImageUrl = useMemo(() => {
    // Priority 1: Server photo URL (final state)
    const shouldUseThumbnail = false;
    // Defaulting to photo URL for preview while thumbnail quality
    // is improved in https://teamequip.atlassian.net/browse/JN-2060
    // Adding this variable now to reduce future code changes when switching again to thumbnail
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (shouldUseThumbnail && !isEmpty(thumbnailUrl)) return thumbnailUrl;
    if (!isEmpty(photoUrl)) return photoUrl;

    // Priority 2: Local photo (persists after upload completes until cache refreshes)
    if (localPhotoUri) {
      return localPhotoUri;
    }

    return undefined;
  }, [localPhotoUri, photoUrl, thumbnailUrl]);

  const SMTEntryDetailsComponent = useMemo(() => {
    const { entryDetailsProps, entryType } = details;

    // using this Record mapper type means that, as SMTEntryType gets new values, this code will be required to be updated
    // for typescript to compile correctly; reducing the chances that handling a new SMTEntryType is overlooked here
    const entryTypeToComponentMapper: Record<
      SMTEntryType,
      (entryDetailsProps: SMTEntryDetailsProps) => JSX.Element
    > = {
      [SMTEntryType.Meal]: MappedMealEntryComponent,
    };

    return entryTypeToComponentMapper[entryType]({
      ...entryDetailsProps,
      testID: baseTestID,
    });
  }, [details, baseTestID]);

  const shouldShowAvatar = !isNil(avatarUserName) || !isNil(avatarUserId);

  return (
    <VStack gap={18}>
      <HStack justifyContent="space-between">
        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- time is guaranteed to be set if not empty */}
        {!isEmpty(time) && <TextTag text={time!} variant="selection" />}
        {shouldShowAvatar && (
          <Avatar
            size="xxs"
            testID={baseTestID}
            userId={avatarUserId ?? ""}
            userName={avatarUserName ?? ""}
            // if missing userType, default to patient background color
            userType={avatarUserType ?? "patient"}
          />
        )}
      </HStack>

      <SMTEntryImagePreview
        isUploading={isUploading}
        onOverrideOpenImageFullScreen={onOverrideOpenImageFullScreen}
        photoContentType={photoContentType}
        photoUrl={photoUrl}
        previewImageUrl={previewImageUrl}
        smtEntryId={smtEntryId}
        testID={baseTestID}
      />

      {SMTEntryDetailsComponent}
    </VStack>
  );
};

export default SMTEntry;
export type { SMTEntryDetails, SMTEntryProps };
